import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const TICKETS_API = '/api/tickets';
const OPERATORS_API = '/api/operators';
const MASTER_DATA_API = '/api/master-data';
const DEPARTMENTS_API = '/api/departments';
const SERVICES_API = '/api/services';
const PREDEFINED_REPLIES_API = '/api/predefined-replies';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    x: ['M18 6 6 18', 'M6 6l12 12'],
    reply: ['M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-11.7 1 1 0 0 1 1 1 8.5 8.5 0 0 1-1.3 4.4l3.8 3.8a1 1 0 0 1 0 1.4z', 'M3 21l1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z'],
    note: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
    bell: ['M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
    tickets: ['M4 19h16', 'M4 15h16', 'M4 11h16', 'M4 7h16'],
    tasks: ['M9 11l3 3L22 4', 'M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'],
    save: ['M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z', 'M17 21v-8H7v8', 'M7 3v5h8'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    tag: ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01'],
    plus: ['M12 5v14', 'M5 12h14'],
    arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7']
};

/* ── Rich text toolbar ── */
function RichToolbar({ editorRef }) {
    const exec = (cmd, val = null) => {
        editorRef.current?.focus();
        document.execCommand(cmd, false, val);
    };
    const btn = (label, action, title) => (
        <button type="button" title={title} onClick={action}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', color: '#374151' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >{label}</button>
    );
    const sep = () => <div style={{ width: '1px', height: '18px', background: '#e2e8f0', margin: '0 2px' }} />;

    return (
        <div style={{ borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
            <div style={{ display: 'flex', gap: '2px', padding: '4px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '13.36px', color: '#374151' }}>
                {['File', 'Edit', 'View', 'Insert', 'Format', 'Tools', 'Table'].map(m => (
                    <button key={m} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', borderRadius: '4px', fontSize: '13.36px', color: '#374151' }}>{m}</button>
                ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '4px 12px', flexWrap: 'wrap' }}>
                <select style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 4px', fontSize: '13.36px', height: '26px', background: '#fff' }} onChange={e => exec('fontName', e.target.value)}>
                    <option value="Inter">System Font</option>
                    <option value="Arial">Arial</option>
                </select>
                <select style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 4px', fontSize: '13.36px', height: '26px', background: '#fff', width: '56px' }} onChange={e => exec('fontSize', e.target.value)}>
                    <option value="3">12pt</option>
                    <option value="4">14pt</option>
                </select>
                {sep()}
                {btn(<b style={{ fontSize: '14.49px' }}>B</b>, () => exec('bold'), 'Bold')}
                {btn(<i style={{ fontSize: '14.49px' }}>I</i>, () => exec('italic'), 'Italic')}
                {sep()}
                {btn(<span style={{ fontSize: '14.49px', fontWeight: 700 }}>A</span>, () => exec('foreColor', '#ff0000'), 'Color')}
                {sep()}
                <div style={{ display: 'flex', gap: '2px' }}>
                    {btn('H1', () => exec('formatBlock', 'H1'), 'Heading 1')}
                    {btn('H2', () => exec('formatBlock', 'H2'), 'Heading 2')}
                </div>
            </div>
        </div>
    );
}

export default function TicketDetails({ ticket: propTicket, user, onBack, onSaved }) {
    const [activeTab, setActiveTab] = useState('reply'); // 'reply', 'note'
    const [ticket, setTicket] = useState(propTicket || {});
    const [form, setForm] = useState(propTicket || {});
    const [replies, setReplies] = useState(propTicket?.replies || []);

    // Safely determine initial status ID
    const getInitialStatus = (t) => {
        const s = t?.status?._id || t?.status;
        if (typeof s === 'string' && s.length === 24 && /^[0-9a-fA-F]{24}$/.test(s)) return s;
        return '';
    }

    const [operators, setOperators] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [services, setServices] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [predefinedReplies, setPredefinedReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyStatus, setReplyStatus] = useState(getInitialStatus(propTicket));
    const [kbLink, setKbLink] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [cc, setCc] = useState('');

    const editorRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                // If ticket is missing (e.g. on refresh), fetch it first
                let currentTicket = ticket;
                const pathParts = window.location.hash.split('/');
                const idFromUrl = pathParts.length > 1 ? pathParts.pop() : null;

                if (!currentTicket?._id && idFromUrl) {
                    const tr = await axios.get(`${TICKETS_API}/${idFromUrl}`);
                    currentTicket = tr.data;
                    setTicket(currentTicket);
                    setReplies(currentTicket.replies || []);
                    setReplyStatus(getInitialStatus(currentTicket));
                }

                // Prepare form state with IDs instead of objects to work with <select>
                const initialForm = {
                    ...currentTicket,
                    department: currentTicket.department?._id || currentTicket.department || '',
                    priority: currentTicket.priority?._id || currentTicket.priority || '',
                    service: currentTicket.service?._id || currentTicket.service || '',
                    tags: Array.isArray(currentTicket.tags) ? currentTicket.tags.join(', ') : currentTicket.tags || ''
                };
                setForm(initialForm);

                const [ops, depts, servs, md, pre] = await Promise.all([
                    axios.get(OPERATORS_API),
                    axios.get(DEPARTMENTS_API),
                    axios.get(SERVICES_API),
                    axios.get(MASTER_DATA_API),
                    axios.get(PREDEFINED_REPLIES_API).catch(() => ({ data: [] }))
                ]);
                setOperators(ops.data);
                setDepartments(depts.data);
                setServices(servs.data);
                setPriorities(md.data.filter(i => i.category === 'ticket-priority'));
                setStatuses(md.data.filter(i => i.category === 'ticket-status'));
                setPredefinedReplies(pre.data);
            } catch (err) {
                console.error("Error fetching data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const handleAddResponse = async () => {
        const content = editorRef.current?.innerHTML || '';
        // Robust check for empty content in rich text editor
        const plainText = content.replace(/<[^>]*>/g, '').trim();
        if (!plainText && !content.includes('<img')) return alert("Content is required");

        const staffName = user ? `${user.firstName} ${user.lastName}` : 'System';

        try {
            const res = await axios.post(`${TICKETS_API}/${ticket?._id}/replies`, {
                user: staffName,
                content: content,
                type: activeTab,
                status: activeTab === 'reply' ? (replyStatus || undefined) : undefined
            });
            setReplies(res.data.replies);
            if (editorRef.current) editorRef.current.innerHTML = '';
            alert(`${activeTab === 'reply' ? 'Response' : 'Note'} added successfully`);
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.message || err.message;
            alert(`Failed to add ${activeTab}: ${msg}`);
        }
    };

    const handleTagClick = (statusName) => {
        const currentTags = typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : (form.tags || []);
        if (currentTags.includes(statusName)) {
            setForm({ ...form, tags: currentTags.filter(t => t !== statusName).join(', ') });
        } else {
            setForm({ ...form, tags: [...currentTags, statusName].join(', ') });
        }
    };

    const handleUpdateInfo = async () => {
        try {
            const payload = {
                subject: form.subject,
                priority: form.priority || undefined,
                department: form.department || undefined,
                service: form.service || undefined,
                tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags
            };
            // Clean payload from empty strings to avoid CastErrors
            Object.keys(payload).forEach(key => {
                if (payload[key] === "") delete payload[key];
            });

            const res = await axios.put(`${TICKETS_API}/${ticket._id}`, payload);
            setTicket(res.data); // Update local ticket state to refresh header etc.
            alert("Ticket information updated");
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.message || err.message;
            alert(`Failed to update ticket: ${msg}`);
        }
    };

    if (loading) return <div style={{ padding: 40, color: '#64748b' }}>Loading ticket details...</div>;

    const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };
    const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', outline: 'none', transition: 'all 0.2s ease', background: '#fff' };
    const selectStyle = { ...inputStyle, cursor: 'pointer' };

    return (
        <div style={{
            background: '#f8fafc',
            height: 'calc(100vh - 65px)',
            display: 'flex',
            overflow: 'hidden',
            color: '#1e293b',
            fontFamily: "'Inter', sans-serif"
        }}>
            {/* Main Conversation Area */}
            <div style={{
                flex: 1,
                padding: '40px',
                overflowY: 'auto',
                scrollBehavior: 'smooth',
                willChange: 'transform'
            }}>
                {/* Clean Modern Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', background: '#fff', padding: '24px 32px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <button onClick={onBack} style={{
                            background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '10px',
                            color: '#64748b', display: 'flex', borderRadius: '12px', transition: 'all 0.2s'
                        }} onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                            <Icon d={icons.arrowLeft} size={18} />
                        </button>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#4f46e5', background: '#eef2ff', padding: '2px 10px', borderRadius: '6px', letterSpacing: '0.05em' }}>
                                    TICKET #{ticket._id?.slice(-4).toUpperCase()}
                                </span>
                                <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>{ticket.status?.name || 'Active'}</span>
                            </div>
                            <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                                {ticket.subject}
                            </h1>
                        </div>
                    </div>
                </div>

                {/* Interaction & History Wrapper */}
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    {/* Focused Reply Area */}
                    <div style={{
                        background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0',
                        overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', marginBottom: '48px'
                    }}>
                        <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '6px' }}>
                            <button onClick={() => setActiveTab('reply')} style={{
                                flex: 1, padding: '12px', border: 'none', cursor: 'pointer', borderRadius: '12px',
                                background: activeTab === 'reply' ? '#fff' : 'transparent',
                                color: activeTab === 'reply' ? '#4f46e5' : '#64748b', fontWeight: '700', fontSize: '14px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                boxShadow: activeTab === 'reply' ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none',
                                transition: 'all 0.2s ease'
                            }}>
                                <Icon d={icons.reply} size={16} /> Public Reply
                            </button>
                            <button onClick={() => setActiveTab('note')} style={{
                                flex: 1, padding: '12px', border: 'none', cursor: 'pointer', borderRadius: '12px',
                                background: activeTab === 'note' ? '#fff' : 'transparent',
                                color: activeTab === 'note' ? '#f59e0b' : '#64748b', fontWeight: '700', fontSize: '14px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                boxShadow: activeTab === 'note' ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none',
                                transition: 'all 0.2s ease'
                            }}>
                                <Icon d={icons.note} size={16} /> Internal Note
                            </button>
                        </div>

                        <div style={{ padding: '32px' }}>
                            {activeTab === 'reply' && (
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <select style={{ ...selectStyle, paddingLeft: '32px', fontSize: '13px', height: '42px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }} defaultValue="" onChange={e => {
                                            const rep = predefinedReplies.find(r => r._id === e.target.value);
                                            if (rep && editorRef.current) editorRef.current.innerHTML = rep.content;
                                        }}>
                                            <option value="">Insert predefined reply...</option>
                                            {predefinedReplies.map(r => <option key={r._id} value={r._id}>{r.title}</option>)}
                                        </select>
                                        <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                            <Icon d={icons.plus} size={14} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                                <RichToolbar editorRef={editorRef} />
                                <div ref={editorRef} contentEditable suppressContentEditableWarning
                                    style={{
                                        minHeight: '220px', padding: '24px', outline: 'none', fontSize: '16px',
                                        lineHeight: '1.8', color: '#334155', background: activeTab === 'note' ? '#fffbeb' : '#fff'
                                    }}
                                />
                            </div>

                            <div style={{ marginTop: '24px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {attachments.map((f, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f5f9',
                                        padding: '6px 14px', borderRadius: '10px', fontSize: '13px', color: '#475569',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <Icon d={icons.paperclip} size={13} />
                                        {f.name}
                                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8' }} onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}>
                                            <Icon d={icons.x} size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginTop: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
                                    {activeTab === 'reply' && (
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <input
                                                style={{ ...inputStyle, paddingLeft: '36px', height: '42px', fontSize: '13px', borderRadius: '12px' }}
                                                value={cc} onChange={e => setCc(e.target.value)}
                                                placeholder="CC: recipient@example.com"
                                            />
                                            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>@</div>
                                        </div>
                                    )}
                                    <button onClick={() => fileInputRef.current?.click()} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
                                        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px',
                                        fontSize: '13px', color: '#64748b', cursor: 'pointer', fontWeight: '700',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <Icon d={icons.paperclip} size={14} /> Attach File
                                    </button>
                                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={e => setAttachments(prev => [...prev, e.target.files[0]])} />
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: '32px' }}>
                                    {activeTab === 'reply' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <select style={{ ...selectStyle, width: '130px', height: '42px', fontSize: '13px', padding: '0 12px', borderRadius: '10px' }} value={replyStatus} onChange={e => setReplyStatus(e.target.value)}>
                                                {statuses.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    <button onClick={handleAddResponse} style={{
                                        padding: '12px 32px', borderRadius: '14px', border: 'none', fontWeight: '800', fontSize: '14px', cursor: 'pointer',
                                        background: activeTab === 'reply' ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : '#f59e0b',
                                        color: '#fff', boxShadow: activeTab === 'reply' ? '0 10px 20px -5px rgba(79, 70, 229, 0.4)' : '0 10px 20px -5px rgba(245, 158, 11, 0.4)',
                                        whiteSpace: 'nowrap', transition: 'transform 0.2s'
                                    }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                                        {activeTab === 'reply' ? 'Post Reply' : 'Log Note'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline History */}
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '26px', top: 0, bottom: 0, width: '2px', background: '#e2e8f0' }} />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                            {/* Original Request Item */}
                            {activeTab === 'reply' && (
                                <div style={{ display: 'flex', gap: '24px', position: 'relative' }}>
                                    <div style={{
                                        width: '54px', height: '54px', borderRadius: '18px', background: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5',
                                        border: '2px solid #e2e8f0', zIndex: 1, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                                    }}>
                                        <Icon d={icons.user} size={22} />
                                    </div>
                                    <div style={{ flex: 1, background: '#fff', padding: '32px', borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                            <div>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{ticket.contactName || 'Client'}</div>
                                                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>{new Date(ticket.createdAt).toLocaleString()}</div>
                                            </div>
                                            <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', background: '#f1f5f9', padding: '4px 10px', borderRadius: '6px', height: 'fit-content' }}>Initial Request</span>
                                        </div>
                                        <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#334155' }} dangerouslySetInnerHTML={{ __html: ticket.description }} />
                                    </div>
                                </div>
                            )}

                            {/* Replies & Notes */}
                            {replies.filter(rep => rep.type === activeTab).map((rep, i) => (
                                <div key={i} style={{ display: 'flex', gap: '24px', position: 'relative' }}>
                                    <div style={{
                                        width: '54px', height: '54px', borderRadius: '18px',
                                        background: rep.type === 'note' ? '#f59e0b' : '#4f46e5',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                                        zIndex: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }}>
                                        <Icon d={rep.type === 'note' ? icons.note : icons.reply} size={22} />
                                    </div>
                                    <div style={{
                                        flex: 1, background: rep.type === 'note' ? '#fffdf5' : '#fff',
                                        padding: '32px', borderRadius: '24px',
                                        border: '1px solid', borderColor: rep.type === 'note' ? '#fde68a' : '#e2e8f0',
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                            <div>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{rep.user}</div>
                                                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>{new Date(rep.createdAt).toLocaleString()}</div>
                                            </div>
                                            <span style={{
                                                fontSize: '11px', fontWeight: '800',
                                                color: rep.type === 'note' ? '#b45309' : '#4f46e5',
                                                textTransform: 'uppercase', letterSpacing: '0.1em',
                                                background: rep.type === 'note' ? '#fef3c7' : '#eef2ff',
                                                padding: '4px 10px', borderRadius: '6px', height: 'fit-content'
                                            }}>
                                                {rep.type === 'note' ? 'Internal' : 'Response'}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#334155' }} dangerouslySetInnerHTML={{ __html: rep.content }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Premium Sidebar */}
            <div style={{
                width: '380px',
                background: '#fff',
                borderLeft: '1px solid #e2e8f0',
                padding: '40px',
                overflowY: 'auto',
                willChange: 'transform'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '900', color: '#0f172a', margin: 0 }}>Ticket Details</h2>
                    <button onClick={handleUpdateInfo} style={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff', border: 'none', borderRadius: '10px',
                        padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                    }}>
                        <Icon d={icons.save} size={14} /> Update
                    </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                    {/* Tags Section */}
                    <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                        <label style={{ ...labelStyle, marginBottom: '16px' }}>Status Labels</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {statuses.map(s => {
                                const curTags = typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : (form.tags || []);
                                const isActive = curTags.includes(s.name);
                                return (
                                    <button key={s._id} type="button" onClick={() => handleTagClick(s.name)}
                                        style={{
                                            padding: '8px 16px', borderRadius: '10px', fontSize: '11px', fontWeight: '800',
                                            cursor: 'pointer', border: '1px solid',
                                            borderColor: isActive ? (s.color || '#4f46e5') : '#e2e8f0',
                                            background: isActive ? (s.color || '#4f46e5') : '#fff',
                                            color: isActive ? '#fff' : '#64748b',
                                            transition: 'all 0.2s',
                                            textTransform: 'uppercase', letterSpacing: '0.05em'
                                        }}
                                    >
                                        {s.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Metadata Section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                        <div>
                            <label style={labelStyle}>Issue Subject</label>
                            <input style={{ ...inputStyle, fontWeight: '700', fontSize: '15px', padding: '12px 16px' }} value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                        </div>

                        <div style={{ background: '#fff', padding: '20px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <label style={{ ...labelStyle, color: '#94a3b8', fontSize: '11px' }}>Requester Profile</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '12px' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', color: '#4f46e5', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '18px' }}>
                                    {ticket.contactName?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div>
                                    <div style={{ fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>{ticket.contactName || 'Anonymous'}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{ticket.contactEmail || 'No contact email'}</div>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                            <div>
                                <label style={labelStyle}>Priority Level</label>
                                <select style={{ ...selectStyle, height: '46px', fontWeight: '600' }} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                                    <option value="">Select Priority</option>
                                    {priorities.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label style={labelStyle}>Department</label>
                                <select style={{ ...selectStyle, height: '46px', fontWeight: '600' }} value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                                    <option value="">Select Department</option>
                                    {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label style={labelStyle}>Service Category</label>
                                <select style={{ ...selectStyle, height: '46px', fontWeight: '600' }} value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}>
                                    <option value="">Select Service</option>
                                    {services.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
